import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionForm from '../components/SubscriptionForm';

const Billing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showSubscriptionForm, setShowSubscriptionForm] = useState(false);
  const [subscriptionPrice, setSubscriptionPrice] = useState('$9.99');
  const [priceId, setPriceId] = useState('');

  useEffect(() => {
    // Get price from environment variable or use default
    const price = import.meta.env.VITE_SUBSCRIPTION_PRICE || '$9.99';
    const id = import.meta.env.VITE_STRIPE_PRICE_ID || '';
    setSubscriptionPrice(price);
    setPriceId(id);

    // Check if subscription was just completed
    if (searchParams.get('subscription') === 'success') {
      // Update subscription status
      localStorage.setItem('subscriptionActive', 'true');
      
      // Get user data for redirect
      const token = user?.uid || localStorage.getItem('authToken') || '';
      const email = user?.email || localStorage.getItem('userEmail') || '';
      
      console.log('User data for redirect:', { token, email, subscriptionActive: true });
      
      if (token && email) {
        setTimeout(() => {
          window.location.href = `https://play.counterscripthq.com?token=${token}&email=${encodeURIComponent(email)}&sub=true`;
        }, 1000);
      } else {
        console.error('Missing auth data for redirect:', { token, email });
        alert('Subscription successful, but missing auth data. Please login again.');
        window.location.href = '/app/login';
      }
    }

    // Check if user already has active subscription
    if (user?.subscriptionActive) {
      navigate('/app');
    }
  }, [user, navigate, searchParams]);

  // Clean URL parameters after they're read
  useEffect(() => {
    if (location.search) {
      navigate(location.pathname, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="card-tactical text-center">
          <p className="text-text-secondary mb-4">Please log in to subscribe.</p>
          <Link to="/app/login" className="btn-primary inline-block">
            Log In
          </Link>
        </div>
      </div>
    );
  }

  if (showSubscriptionForm) {
    return (
      <div className="min-h-screen py-16 px-4">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <button
              onClick={() => setShowSubscriptionForm(false)}
              className="text-text-secondary hover:text-copper transition-colors font-mono text-sm mb-4 inline-block"
            >
              ← Back to Plans
            </button>
            <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              Subscribe to COUP EDU
            </h1>
            <p className="text-text-secondary text-lg">
              Complete your subscription to access the Data Hub
            </p>
          </div>

          <SubscriptionForm price={subscriptionPrice} priceId={priceId} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-16 px-4">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <div className="w-20 h-20 border-2 border-copper flex items-center justify-center mx-auto mb-6">
            <svg className="w-12 h-12 text-copper" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Subscription Required
          </h1>
          <p className="text-text-secondary text-lg max-w-2xl mx-auto">
            Start your <strong className="text-copper">7-day free trial</strong> of COUP EDU. 
            You won't be charged today. After 7 days, your subscription continues at {subscriptionPrice}/month. 
            Cancel anytime during the trial to avoid charges.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {/* Individual Plan */}
          <div className="card-tactical relative">
            <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
              <span className="bg-copper text-white px-4 py-1 text-sm font-display font-semibold uppercase tracking-wider">
                Recommended
              </span>
            </div>
            <div className="text-center pt-6">
              <h2 className="text-2xl font-display font-bold text-white mb-2">
                Individual Teacher
              </h2>
              <p className="text-text-secondary mb-6 text-sm">
                Perfect for single classrooms
              </p>
              <div className="mb-8">
                <div className="text-center mb-2">
                  <span className="text-sm font-mono text-copper uppercase tracking-wider">
                    7-Day Free Trial
                  </span>
                </div>
                <div className="text-center">
                  <span className="text-5xl font-display font-bold text-white">{subscriptionPrice}</span>
                  <span className="text-text-secondary font-mono">/month</span>
                </div>
                <p className="text-text-muted text-sm text-center mt-2">
                  $0 today, then {subscriptionPrice}/month after trial
                </p>
              </div>
              <ul className="space-y-3 mb-8 text-left">
                <li className="flex items-start">
                  <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">
                    <strong className="text-copper">7-day free trial</strong> - card required, cancel anytime
                  </span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">Full COUP Data Hub access</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">Live game controls and timers</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">Faction management tools</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">Create custom prompts</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 mr-3 flex-shrink-0" style={{ color: '#10B981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">Cancel anytime, no commitment</span>
                </li>
              </ul>
              <p className="text-text-muted text-sm mt-4 font-mono text-center">
                Credit card required for free trial
              </p>
              <button
                onClick={() => setShowSubscriptionForm(true)}
                className="btn-primary w-full"
              >
                Subscribe Now
              </button>
              <div className="mt-4 space-y-2">
                <p className="text-text-secondary text-sm font-mono text-center">
                  <span className="text-scan-green font-semibold">7-day free trial</span> • Credit card required
                </p>
                <p className="text-tactical-gray text-xs font-mono text-center">
                  Your card will be charged automatically after the 7-day trial ends unless you cancel before then.
                </p>
              </div>
            </div>
          </div>

          {/* District Plan */}
          <div className="card-tactical">
            <div className="text-center">
              <h2 className="text-2xl font-display font-bold text-white mb-2">
                District Licensing
              </h2>
              <p className="text-text-secondary mb-6 text-sm">
                For schools and districts
              </p>
              <div className="mb-8">
                <span className="text-5xl font-display font-bold text-white">Custom</span>
              </div>
              <ul className="space-y-3 mb-8 text-left">
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-scan-green mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">Everything in Individual</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-scan-green mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">Unlimited teachers</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-scan-green mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">District analytics</span>
                </li>
                <li className="flex items-start">
                  <svg className="w-6 h-6 text-scan-green mr-3 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-text-secondary">Custom training</span>
                </li>
              </ul>
              <Link to="/contact" className="btn-secondary w-full block text-center">
                Contact for Quote
              </Link>
            </div>
          </div>
        </div>

        <div className="text-center">
          <Link to="/app" className="text-text-secondary hover:text-copper transition-colors font-mono text-sm">
            ← Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Billing;
